import { useEffect, useMemo, useState } from "react";

const gradientCache = new Map();

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((value) => clampChannel(value).toString(16).padStart(2, "0")).join("")}`;
}

function hexToRgb(hex) {
  const value = String(hex || "").replace(/^#/, "");
  if (!/^[a-f0-9]{6}$/i.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }) {
  const red = clampChannel(r) / 255;
  const green = clampChannel(g) / 255;
  const blue = clampChannel(b) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: lightness };
  }

  const delta = max - min;
  const saturation = lightness > 0.5
    ? delta / (2 - max - min)
    : delta / (max + min);

  let hue = 0;
  if (max === red) {
    hue = (green - blue) / delta + (green < blue ? 6 : 0);
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }

  return { h: hue / 6, s: saturation, l: lightness };
}

function hslToRgb({ h, s, l }) {
  if (s === 0) {
    const value = clampChannel(l * 255);
    return { r: value, g: value, b: value };
  }

  const hueToRgb = (p, q, t) => {
    let next = t;
    if (next < 0) next += 1;
    if (next > 1) next -= 1;
    if (next < 1 / 6) return p + (q - p) * 6 * next;
    if (next < 1 / 2) return q;
    if (next < 2 / 3) return p + (q - p) * (2 / 3 - next) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: clampChannel(hueToRgb(p, q, h + 1 / 3) * 255),
    g: clampChannel(hueToRgb(p, q, h) * 255),
    b: clampChannel(hueToRgb(p, q, h - 1 / 3) * 255),
  };
}

function boostSaturation(color, amount) {
  const hsl = rgbToHsl(color);
  return hslToRgb({
    ...hsl,
    s: Math.max(0, Math.min(1, hsl.s + amount)),
  });
}

function adjustLightness(color, amount) {
  const hsl = rgbToHsl(color);
  return hslToRgb({
    ...hsl,
    l: Math.max(0, Math.min(1, hsl.l + amount)),
  });
}

function mixColors(base, target, ratio) {
  return {
    r: clampChannel(base.r + (target.r - base.r) * ratio),
    g: clampChannel(base.g + (target.g - base.g) * ratio),
    b: clampChannel(base.b + (target.b - base.b) * ratio),
  };
}

function colorDistance(a, b) {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function isUsablePaletteColor(color) {
  const { s, l } = rgbToHsl(color);
  if (s < 0.16) return false;
  if (l < 0.12 || l > 0.88) return false;
  return true;
}

function boostColorVibrance(color) {
  const saturated = boostSaturation(color, 0.16);
  const { l } = rgbToHsl(saturated);
  if (l < 0.24) return adjustLightness(saturated, 0.08);
  if (l > 0.72) return adjustLightness(saturated, -0.06);
  return saturated;
}

function pickTwoDistinctPaletteColors(palette) {
  const usable = palette
    .map((entry) => ({
      ...entry,
      boosted: boostColorVibrance(entry.color),
    }))
    .filter((entry) => isUsablePaletteColor(entry.boosted))
    .sort((a, b) => b.weight - a.weight);

  if (!usable.length) return null;

  const first = usable[0].boosted;
  const firstHsl = rgbToHsl(first);

  const secondEntry = usable.find((entry, index) => {
    if (index === 0) return false;
    const candidate = entry.boosted;
    const candidateHsl = rgbToHsl(candidate);
    const hueDistance = Math.min(
      Math.abs(firstHsl.h - candidateHsl.h),
      1 - Math.abs(firstHsl.h - candidateHsl.h)
    );
    return (
      colorDistance(first, candidate) >= 96 &&
      hueDistance >= 0.08 &&
      Math.abs(firstHsl.l - candidateHsl.l) >= 0.03
    );
  });

  if (!secondEntry) return null;
  return [first, secondEntry.boosted];
}

function buildTwoColorGradient(colorA, colorB) {
  return `linear-gradient(135deg, ${rgbToHex(colorA)} 0%, ${rgbToHex(colorB)} 100%)`;
}

function buildGradientPalette(colorA, colorB) {
  const glowBase = mixColors(colorA, colorB, 0.5);

  return {
    gradient: buildTwoColorGradient(colorA, colorB),
    colorA: rgbToHex(colorA),
    colorB: rgbToHex(colorB),
    glow: `rgba(${glowBase.r}, ${glowBase.g}, ${glowBase.b}, 0.52)`,
  };
}

function extractPaletteFromImageSource(src, maxSize = 200) {
  return new Promise((resolve, reject) => {
    if (!src || typeof window === "undefined") {
      reject(new Error("No image source"));
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";

    image.onload = () => {
      try {
        const scale = Math.min(1, maxSize / Math.max(image.naturalWidth || 1, image.naturalHeight || 1));
        const width = Math.max(1, Math.round((image.naturalWidth || 1) * scale));
        const height = Math.max(1, Math.round((image.naturalHeight || 1) * scale));
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) {
          reject(new Error("Canvas unavailable"));
          return;
        }

        canvas.width = width;
        canvas.height = height;
        context.drawImage(image, 0, 0, width, height);
        const { data } = context.getImageData(0, 0, width, height);
        const buckets = new Map();

        for (let index = 0; index < data.length; index += 16) {
          const alpha = data[index + 3];
          if (alpha < 80) continue;

          const r = data[index];
          const g = data[index + 1];
          const b = data[index + 2];
          const brightness = (r + g + b) / 3;
          const saturation = Math.max(r, g, b) - Math.min(r, g, b);
          const weight = (alpha / 255) * (0.8 + saturation / 200) * (brightness > 245 ? 0.28 : 1);
          const bucketKey = [
            Math.round(r / 32),
            Math.round(g / 32),
            Math.round(b / 32),
          ].join(":");

          const current = buckets.get(bucketKey) || {
            r: 0,
            g: 0,
            b: 0,
            weight: 0,
          };
          current.r += r * weight;
          current.g += g * weight;
          current.b += b * weight;
          current.weight += weight;
          buckets.set(bucketKey, current);
        }

        const palette = Array.from(buckets.values())
          .filter((entry) => entry.weight > 0)
          .map((entry) => ({
            color: {
              r: entry.r / entry.weight,
              g: entry.g / entry.weight,
              b: entry.b / entry.weight,
            },
            weight: entry.weight,
          }))
          .sort((a, b) => b.weight - a.weight);

        const selectedColors = pickTwoDistinctPaletteColors(palette);
        if (!selectedColors) {
          reject(new Error("No readable pixels"));
          return;
        }

        resolve(buildGradientPalette(selectedColors[0], selectedColors[1]));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    image.src = src;
  });
}

async function resolveGradientPalette(sources, maxSize) {
  for (const src of sources) {
    const key = `${src}|${maxSize}`;
    if (gradientCache.has(key)) {
      return gradientCache.get(key);
    }

    try {
      const palette = await extractPaletteFromImageSource(src, maxSize);
      gradientCache.set(key, palette);
      return palette;
    } catch {
      continue;
    }
  }

  throw new Error("Unable to extract palette");
}

export function useAdaptiveHeroGradient(imageSources, fallbackGradient, options = {}) {
  const maxSize = Number.isFinite(Number(options.maxSize)) ? Number(options.maxSize) : 200;
  const normalizedSources = useMemo(
    () => Array.from(new Set((Array.isArray(imageSources) ? imageSources : []).filter(Boolean))),
    [imageSources]
  );
  const cacheKey = useMemo(() => normalizedSources.join("|"), [normalizedSources]);
  const [palette, setPalette] = useState(null);

  useEffect(() => {
    let active = true;

    if (!normalizedSources.length) {
      setPalette(null);
      return () => {
        active = false;
      };
    }

    resolveGradientPalette(normalizedSources, maxSize)
      .then((nextPalette) => {
        if (active) setPalette(nextPalette);
      })
      .catch(() => {
        if (active) setPalette(null);
      });

    return () => {
      active = false;
    };
  }, [cacheKey, maxSize, normalizedSources]);

  return useMemo(() => {
    const background = palette
      ? palette.gradient
      : fallbackGradient;

    return {
      background,
      glowColor: palette ? palette.glow : "rgba(255,255,255,0.18)",
      hasAdaptiveColor: Boolean(palette),
    };
  }, [fallbackGradient, palette]);
}
